/**
 * Franchise Management Type Definitions
 *
 * These types match the backend API responses from distill-hq-server
 */

export interface Enterprise {
  id: string;
  name: string;
  description?: string;
  logoUrl?: string;
  createdAt: string;
  updatedAt: string;
}

export interface FranchiseBrand {
  id: string;
  name: string;
  description?: string;
  industry?: string;
  enterpriseId: string;
  createdAt: string;
  updatedAt: string;
  enterprise?: {
    name: string;
  };
  branding?: BrandBranding;
  settings?: BrandSettings;
  _count?: {
    tenants: number;
    regions: number;
    workflows: number;
    serviceTemplates: number;
  };
}

export interface FranchiseRegion {
  id: string;
  name: string;
  description?: string;
  brandId: string;
  createdAt: string;
  updatedAt: string;
  brand?: {
    name: string;
    enterprise?: {
      name: string;
    };
  };
  _count?: {
    tenants: number;
  };
}

export interface BrandBranding {
  id: string;
  brandId: string;
  brandName: string;
  logoUrl?: string;
  faviconUrl?: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor?: string;
  backgroundColor?: string;
  textColor?: string;
  headerGradient?: string;
  buttonGradient?: string;
  cardGradient?: string;
  fontFamily?: string;
  customCSS?: string;
  createdAt: string;
  updatedAt: string;
}

export interface BrandSettings {
  id: string;
  brandId: string;
  primaryTaxRate?: number;
  primaryTaxName?: string;
  secondaryTaxRate?: number;
  secondaryTaxName?: string;
  currency?: string;
  timeZone?: string;
  billPrefix?: string;
  invoicePrefix?: string;
  defaultRoyaltyRate?: number;
  portalWelcomeMessage?: string;
  portalRequestConfirmationMessage?: string;
  createdAt: string;
  updatedAt: string;
}

export interface EnterpriseMetrics {
  enterprise: {
    id: string;
    name: string;
    description?: string;
  };
  summary: {
    totalBrands: number;
    totalRegions: number;
    totalFranchisees: number;
    averageComplianceScore: number | null;
  };
  revenue: {
    totalRevenue: number;
    totalRoyalties: number;
    averageRoyaltyRate: number | null;
  };
  invoices: {
    total: number;
    paid: number;
    totalRevenue: number;
  };
}

export interface BrandMetrics {
  brand: {
    id: string;
    name: string;
    industry?: string;
    enterpriseName: string;
  };
  summary: {
    totalRegions: number;
    totalFranchisees: number;
    averageComplianceScore: number | null;
  };
  revenue: {
    totalRevenue: number;
    totalRoyalties: number;
  };
  invoices: {
    total: number;
    paid: number;
    totalRevenue: number;
  };
}

export interface RegionMetrics {
  region: {
    id: string;
    name: string;
    brandName: string;
    enterpriseName: string;
  };
  summary: {
    totalFranchisees: number;
    averageComplianceScore: number | null;
  };
  revenue: {
    totalRevenue: number;
    totalRoyalties: number;
  };
  invoices: {
    total: number;
    paid: number;
    totalRevenue: number;
  };
}

export interface TenantMetrics {
  tenant: {
    code: string;
    name: string;
    enterpriseName?: string;
    brandName?: string;
    regionName?: string;
    franchiseeOwner?: string;
    franchiseeEmail?: string;
    franchiseePhone?: string;
  };
  compliance: {
    score: number | null;
  };
  revenue: {
    totalRevenue: number;
    royaltyRate: number | null;
    royaltiesOwed: number;
  };
  counts: {
    customers: number;
    invoices: number;
    bills: number;
  };
  invoices: {
    total: number;
    paid: number;
    totalRevenue: number;
  };
  recentInvoices: Array<{
    invoiceNumber: string;
    issuedDate: string | null;
    dueDate: string | null;
    state: string;
    totalAmount: number;
    customerName?: string;
  }>;
}

export interface FranchiseTenant {
  code: string;
  name: string;
  description?: string;
  enterpriseId?: string;
  brandId?: string;
  regionId?: string;
  franchiseeOwner?: string;
  franchiseeEmail?: string;
  franchiseePhone?: string;
  complianceScore?: number;
  royaltyRate?: number;
  brand?: {
    name: string;
    industry?: string;
  };
  region?: {
    name: string;
  };
}

// ─── Dashboard Zone Types ───────────────────────────────────────

export interface VitalSignsData {
  systemWideSales: {
    value: number;
    delta: number;
    direction: 'up' | 'down' | 'stable';
  };
  royaltyIntegrity: {
    value: number;
    direction: 'up' | 'down' | 'stable';
  };
  activeWorkOrders: {
    count: number;
    delta: number;
    direction: 'up' | 'down' | 'stable';
  };
  networkNPS: {
    score: number;
    delta: number;
    direction: 'up' | 'down' | 'stable';
  };
}

export interface AUVTrendPoint {
  month: string;
  auv: number;
  target: number;
}

export interface OnboardingEntry {
  franchisee: string;
  days: number;
  target: number;
  brandColor: string;
}

export interface ARAgingEntry {
  franchisee: string;
  amountDue: number;
  daysOverdue: number;
  bucket: '30-60' | '60+' | '90+';
  type: string;
}

export interface LeadFunnelStage {
  stage: string;
  count: number;
  color: string;
}

export interface LedgerSyncData {
  syncPercentage: number;
  lastSyncTime: string;
  totalUnits: number;
  syncedUnits: number;
  flaggedUnits: Array<{
    name: string;
    reason: string;
    lastSync: string;
  }>;
}

// ─── Helpers ────────────────────────────────────────────────────

/**
 * Helper function to determine compliance status based on score
 */
export const getComplianceStatus = (score: number | null | undefined): {
  status: 'excellent' | 'good' | 'warning' | 'critical' | 'unknown';
  color: string;
  label: string;
} => {
  if (score === null || score === undefined) {
    return { status: 'unknown', color: '#9E9E9E', label: 'N/A' };
  }

  if (score >= 95) {
    return { status: 'excellent', color: '#4CAF50', label: 'Excellent' };
  } else if (score >= 90) {
    return { status: 'good', color: '#8BC34A', label: 'Good' };
  } else if (score >= 85) {
    return { status: 'warning', color: '#FF9800', label: 'Needs Attention' };
  } else {
    return { status: 'critical', color: '#F44336', label: 'Critical' };
  }
};

/**
 * Helper function to format currency
 */
export const formatCurrency = (amount: number, currency: string = 'USD'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount);
};

/**
 * Helper function to format percentage
 */
export const formatPercentage = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return 'N/A';
  return `${(value * 100).toFixed(2)}%`;
};
